import { useBulkUpdateStudentSettingsMutation } from '@/app/redux/endpoints/students';
import { useCurrentOrganisationId } from '@/app/redux/hooks/organisation';
import { useUser } from '@/app/redux/hooks/user';
import {
  AudioDelay,
  ControllerType,
  GameSettings,
  Student,
} from '@/common/types/student';
import { UNKNOWN_ERROR_MESSAGE } from '@/common/ui/UnknownErrorMessage';
import { initialGameSettings } from '@/common/utils/student';
import LearnerSettings from '@/dashboard/LearnerView/LearnerSettings';
import { Button } from '@shadcn/components/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@shadcn/components/dialog';
import { chunk } from 'lodash';
import { useState } from 'react';
import { toast } from 'sonner';

interface BulkSettingsDialogProps {
  open: boolean;
  onClose: () => void;
  students: Student[];
}

export default function BulkSettingsDialog({
  open,
  onClose,
  students,
}: BulkSettingsDialogProps) {
  const { currentOrganisationId } = useCurrentOrganisationId();
  const { user } = useUser();

  const [bulkUpdateStudentSettings] = useBulkUpdateStudentSettingsMutation();

  const initialSettings =
    students.length > 0 ? students[0].settings : initialGameSettings;

  const [settings, setSettings] = useState<GameSettings>(initialSettings);
  const [isLoading, setIsLoading] = useState(false);

  function handlePlayPhonemeChanged(value: boolean) {
    setSettings({ ...settings, playPhonemeOnCardDrop: value });
  }
  function handleControllerTypeChange(value: ControllerType) {
    setSettings({ ...settings, controllerType: value });
  }
  function handleDuelsEnabledChange(value: boolean) {
    setSettings({ ...settings, duelsEnabled: value });
  }
  function handleAudioDelayChange(value: AudioDelay) {
    setSettings({
      ...settings,
      audio: { ...settings.audio, audioDelay: value },
    });
  }
  function handleDistractorCardsChange(value: boolean) {
    setSettings({ ...settings, distractorCardsEnabled: value });
  }
  function handleWordLimitChange(value: number) {
    setSettings({ ...settings, wordLimit: value });
  }
  function handleWordSegmentationChange(value: boolean) {
    setSettings({
      ...settings,
      wordSegmentationEnabled: value,
      audio: {
        ...settings.audio,
        audioDelay: value ? settings.audio.audioDelay : AudioDelay.SHORT,
      },
    });
  }
  function handleArticulatoryGesturesChange(value: boolean) {
    setSettings({ ...settings, articulatoryGesturesEnabled: value });
  }

  async function handleSave() {
    if (!currentOrganisationId) {
      console.error('Missing org ID');
      toast.error('Failed to save settings', {
        description: UNKNOWN_ERROR_MESSAGE,
      });
      return;
    }

    setIsLoading(true);
    const studentIds = students.map((s) => s.id);
    const chunks = chunk(studentIds, 50);
    let applied = 0;

    const toastId = toast.loading('Applying settings...', {
      description: "Please don't refresh or leave the page",
    });
    onClose();

    try {
      for (const idChunk of chunks) {
        await bulkUpdateStudentSettings({
          studentIds: idChunk,
          userName: user?.name ?? 'Educator',
          organisationId: currentOrganisationId,
          settings,
          location: 'bulk-settings',
        });
        applied += idChunk.length;
        toast.loading(`Applying settings (${applied}/${studentIds.length})`, {
          id: toastId,
        });
      }

      toast.success('Settings applied', {
        id: toastId,
        description: '',
      });
    } catch (e) {
      console.error('Error applying settings:', e);
      if (applied > 0) {
        toast.error('Failed to apply settings', {
          id: toastId,
          description: `Applied to ${applied} of ${studentIds.length} learners. Please retry.`,
        });
      } else {
        toast.error('Failed to apply settings', {
          id: toastId,
          description: UNKNOWN_ERROR_MESSAGE,
        });
      }
    } finally {
      setIsLoading(false);
    }
  }

  return students.length ? (
    <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
      <DialogContent onClick={(e) => e.stopPropagation()} className="max-w-xl">
        <DialogHeader>
          <DialogTitle>
            Change Settings for{' '}
            {students[0].studentFirstName + ' ' + students[0].studentSurname}{' '}
            {students.length > 1
              ? `+ ${students.length - 1} other${
                  students.length - 1 === 1 ? '' : 's'
                }`
              : ''}
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-2">
          <LearnerSettings
            settings={settings}
            onPlayPhonemeChanged={handlePlayPhonemeChanged}
            onControllerTypeChange={handleControllerTypeChange}
            onDuelsEnabledChange={handleDuelsEnabledChange}
            onAudioDelayChange={handleAudioDelayChange}
            onDistractorCardsChange={handleDistractorCardsChange}
            onWordLimitChange={handleWordLimitChange}
            onWordSegmentationChange={handleWordSegmentationChange}
            onArticulatoryGesturesChange={handleArticulatoryGesturesChange}
            disabled={isLoading}
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleSave} loading={isLoading}>
            Confirm
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  ) : null;
}
